/**
 * The permission registry (SECURITY §5).
 *
 * Permission strings are `module.resource.action`, **declared here and never
 * invented at a call site**. An unregistered permission fails CI, and a handler
 * that declares no permission is unreachable rather than public — deny by
 * default means the absence of a decision is a refusal, not a permission.
 *
 * Stage 2 declares **Platform Core permissions only**. Not one business module
 * appears below, because not one exists: a permission for a module nobody has
 * built is a claim about a design nobody has approved.
 *
 * This list is mirrored by migrations 0008 and 0021, and a test asserts the two
 * are identical, so the catalogue the database enforces and the catalogue the
 * code checks cannot drift apart.
 */
function permission(key, description, options = {}) {
    const [moduleKey, resource, action] = key.split('.');
    if (moduleKey === undefined || resource === undefined || action === undefined) {
        throw new Error(`Permission ${key} is not module.resource.action`);
    }
    return {
        key,
        moduleKey,
        resource,
        action,
        description,
        ...(options.mfaRequired === undefined ? {} : { mfaRequired: options.mfaRequired }),
        ...(options.stepUpRequired === undefined ? {} : { stepUpRequired: options.stepUpRequired }),
    };
}
export const PERMISSIONS = [
    // ---- tenant and hierarchy -------------------------------------------------
    permission('platform.tenant.read', 'View this tenant’s profile and lifecycle state'),
    permission('platform.tenant.update', 'Change this tenant’s display name, locale or time zone'),
    permission('platform.org_node.read', 'View the organisational hierarchy'),
    permission('platform.org_node.create', 'Add an organisation, legal entity, business unit or site'),
    permission('platform.org_node.update', 'Rename or re-code a hierarchy node'),
    permission('platform.org_node.close', 'Close a hierarchy node with an effective date'),
    // ---- identity ---------------------------------------------------------------
    permission('platform.user.read', 'View users in this tenant'),
    permission('platform.user.invite', 'Invite a user to this tenant'),
    permission('platform.user.update', 'Change a user’s name or details'),
    permission('platform.user.deactivate', 'Deactivate a user', { mfaRequired: true }),
    permission('platform.session.read', 'View active sessions'),
    permission('platform.session.revoke', 'Revoke a session, or sign a user out everywhere'),
    // ---- authorisation ----------------------------------------------------------
    // Permission administration is in the mandatory-MFA set and the step-up set:
    // changing who can do what is the action an attacker most wants.
    permission('platform.role.read', 'View roles and their permissions'),
    permission('platform.role.create', 'Create or adapt a role', {
        mfaRequired: true,
        stepUpRequired: true,
    }),
    permission('platform.role.assign', 'Assign a role to a user at a hierarchy node', {
        mfaRequired: true,
        stepUpRequired: true,
    }),
    permission('platform.role.revoke', 'Revoke a role assignment', {
        mfaRequired: true,
        stepUpRequired: true,
    }),
    permission('platform.separation_of_duties.manage', 'Define which roles may not be held together', {
        mfaRequired: true,
        stepUpRequired: true,
    }),
    // ---- partner elevation (OD-8) ------------------------------------------------
    permission('platform.partner_elevation.read', 'View the live register of partner elevations'),
    permission('platform.partner_elevation.grant', 'Grant a time-boxed partner elevation', {
        mfaRequired: true,
        stepUpRequired: true,
    }),
    permission('platform.partner_elevation.revoke', 'Revoke a partner elevation immediately'),
    // ---- security policy ----------------------------------------------------------
    permission('platform.security_policy.read', 'View this tenant’s MFA and session policy'),
    permission('platform.security_policy.update', 'Change this tenant’s MFA and session policy', {
        mfaRequired: true,
        stepUpRequired: true,
    }),
    // ---- audit ----------------------------------------------------------------------
    permission('platform.audit.read', 'Read this tenant’s audit trail'),
    permission('platform.audit.verify', 'Verify the audit hash chain'),
    // ---- entitlement ------------------------------------------------------------------
    permission('platform.entitlement.read', 'View which modules this tenant is entitled to'),
    permission('platform.entitlement.grant', 'Manually grant or extend an entitlement', {
        mfaRequired: true,
        stepUpRequired: true,
    }),
    permission('platform.entitlement.revoke', 'Manually revoke an entitlement', {
        mfaRequired: true,
        stepUpRequired: true,
    }),
    // ---- shared business foundations (Stage 3) -------------------------------
    // Administration of the kernel services Finance and every other module will
    // sit on. Still Platform Core: a numbering series and an exchange rate are
    // not Finance's to own (ADR-0028, DOMAIN_BOUNDARIES §4.3).
    permission('platform.number_series.read', 'View document numbering series and their formats'),
    // MFA-mandatory: editing a live series changes how statutory documents are
    // numbered, and a format changed mid-life is how two documents end up
    // sharing a number.
    permission('platform.number_series.manage', 'Create or change a document numbering series for a legal entity', { mfaRequired: true }),
    permission('platform.number_allocation.read', 'View which document numbers have been issued'),
    permission('platform.tenant_currency.read', 'View which currencies this tenant transacts in'),
    permission('platform.tenant_currency.manage', 'Enable a currency for this tenant, or set its base currency'),
    permission('platform.exchange_rate.read', 'View exchange rates and where they came from'),
    permission('platform.exchange_rate.manage', 'Record a tenant-specific or contractual exchange rate'),
    permission('platform.tax_code.read', 'View tax codes and the rates in force on a date'),
    permission('platform.attachment.read', 'View the files attached to a record'),
    permission('platform.attachment.attach', 'Attach a file to a record'),
    permission('platform.attachment.remove', 'Detach a file from a record'),
    // ---- Stage 5: shared collaboration and workflow ---------------------------
    // Still Platform Core. A task, an approval and a comment are kernel concerns
    // that every module sits on — the argument ADR-0028 makes for numbering and
    // currency — so they are declared once here rather than eight times.
    permission('platform.task.read', 'View tasks within your scope'),
    permission('platform.task.create', 'Raise a task for somebody to do'),
    permission('platform.task.assign', 'Assign a task to a person or a role'),
    permission('platform.task.reassign', 'Move a task to somebody else, or delegate it'),
    permission('platform.task.complete', 'Mark a task you hold as done'),
    permission('platform.task.cancel', 'Cancel a task that is no longer needed'),
    permission('platform.approval.read', 'View approvals you have been asked for'),
    // NOT step-up, deliberately. An inbox worked through in one sitting would
    // demand a fresh proof of presence per decision, which teaches people to
    // dismiss the prompt — and a prompt dismissed reflexively protects nothing.
    // What an approval needs is attribution, and every decision has it.
    permission('platform.approval.act', 'Approve, reject or send back an approval you hold'),
    permission('platform.workflow.read', 'View workflow definitions and running instances'),
    // MFA-mandatory: activating a version changes who may approve what across
    // every future instance. That is a privilege change in configuration's
    // clothing.
    permission('platform.workflow.manage', 'Create, version, activate or deactivate a workflow definition', { mfaRequired: true }),
    permission('platform.workflow.start', 'Start a workflow over a record'),
    permission('platform.workflow.cancel', 'Cancel a running workflow instance'),
    permission('platform.comment.read', 'Read comments on records within your scope'),
    permission('platform.comment.create', 'Comment on a record, and mention colleagues'),
    permission('platform.comment.delete_own', 'Delete a comment you wrote'),
    // Separate from delete_own, and rarer: removing somebody else's words is a
    // moderation act, not an editing one.
    permission('platform.comment.delete_any', 'Delete any comment in this tenant'),
    permission('platform.watcher.read', 'See who is watching a record'),
    permission('platform.watcher.manage', 'Follow or unfollow a record, and manage its watchers'),
    permission('platform.activity.read', 'Read the activity timeline of a record'),
    // ---- Stage 6: search, reporting, exports, metrics -------------------------
    // Platform Core throughout. What the Reports & Analytics add-on sells is the
    // BUILDER, not the data (C-2), so `report.manage` is a Core permission an
    // unentitled tenant simply never gets to exercise — the 404 comes from
    // entitlement rather than from a missing permission.
    permission('platform.search.query', 'Search records you are permitted to see'),
    permission('platform.search.save', 'Save a search, and share it with the tenant'),
    permission('platform.report.read', 'View report definitions'),
    permission('platform.report.run', 'Run a report over records you are permitted to see'),
    // MFA-mandatory, and not because a report is dangerous. A report definition
    // decides what a whole tenant then treats as a number, and it is the natural
    // place to quietly widen a date range or drop a filter — a configuration
    // change with reporting consequences, which is the argument workflow.manage
    // makes for itself.
    permission('platform.report.manage', 'Create, edit and version report definitions', {
        mfaRequired: true,
    }),
    permission('platform.export.create', 'Export records you are permitted to see'),
    permission('platform.export.read', 'View and download your own exports'),
    permission('platform.export.schedule', 'Create and manage scheduled exports'),
    permission('platform.metric.read', 'View dashboard metrics'),
    permission('platform.projection.read', 'View read-model and index health'),
    // Rebuilding is cheap to ask for and expensive to serve, and a read model
    // being rebuilt is a read model briefly disagreeing with itself.
    permission('platform.projection.rebuild', 'Rebuild a read model or search index', {
        mfaRequired: true,
    }),
    // ---- Stage 7: the AI gateway ---------------------------------------------
    // Platform Core, all of them, and that is a commercial decision as much as a
    // technical one. C-2 makes the gateway mandatory shared Core: every module
    // routes through it and none may reach a provider directly. Behind the
    // `ai-automation` SKU, a tenant without that SKU would have a platform whose
    // AI CONTROLS were absent rather than a platform without AI — and the first
    // module wanting a summary would be pushed toward calling a provider itself.
    // What the add-on still sells is agents, workflow design and cross-module
    // orchestration; asking a question and getting a permission-filtered answer
    // is Core.
    permission('platform.ai.ask', 'Ask TESSMA AI about records you are permitted to see'),
    // NOT step-up, and the instinct to make it so is worth naming. Asking the AI
    // to propose something writes a row and changes nothing else. The dangerous
    // moment is CONFIRMATION, and confirmation is not governed by this permission
    // at all: it re-enters the ordinary service and demands the ordinary
    // permission for the ordinary action, at its own MFA and step-up settings.
    // Step-up here would add friction at the harmless step and imply the real
    // check had already happened.
    permission('platform.ai.act', 'Ask TESSMA AI to propose an action, and review the proposals it makes'),
    permission('platform.ai.read', 'View your own AI conversations and their sources'),
    // Tenant-wide oversight, and deliberately NOT a way to read other people's
    // conversations. It grants the PROOF — who asked, under which prompt, what it
    // cost, which records entered context, what was refused — and never the
    // words. An auditor needs to verify the controls held, not to read a
    // colleague's question.
    permission('platform.ai.audit', 'Review tenant-wide AI activity: requests, sources, refusals and cost'),
    // MFA-mandatory, on the same argument report.manage and workflow.manage make.
    // This is not a record change: it decides WHICH MODELS THIS TENANT'S DATA MAY
    // BE SENT TO and how much may be spent doing it. It is the natural place to
    // quietly allow a model somebody was not supposed to use, and the change
    // would be invisible in the records it affects.
    permission('platform.ai.manage', 'Manage AI budgets and model policy for the tenant', {
        mfaRequired: true,
    }),
    // ---- Stage 8: Party Master, shared Platform Core -------------------------
    // Party is canonical identity shared by every module (D-2), so its
    // permissions are `platform.*` and not `finance.*` — CRM will hold the same
    // two without buying Finance.
    permission('platform.party.read', 'View parties: customers, suppliers and their identity'),
    permission('platform.party.manage', 'Create, amend and merge parties'),
    permission('platform.catalogue.read', 'View the shared product and service catalogue'),
    permission('platform.catalogue.manage', 'Maintain the shared product and service catalogue'),
    // ---- Stage 8: Finance ----------------------------------------------------
    // THE FIRST COMMERCIAL MODULE'S PERMISSIONS, and the first time this registry
    // holds anything that is not Platform Core. The module key is `finance`,
    // which is what makes entitlement bite before permission does: an unentitled
    // tenant gets 404 from the entitlement check and never reaches these.
    //
    // The split is by CONSEQUENCE, not by screen. Reading a bill and approving
    // one are different permissions because they are different acts; approving
    // and posting are separate because FR-APB-006 and FR-APR-003 require that
    // one person can be allowed to do one and not the other.
    permission('finance.setup.manage', 'Configure financial years, periods, terms and settings', {
        mfaRequired: true,
    }),
    permission('finance.account.read', 'View the chart of accounts'),
    permission('finance.account.manage', 'Maintain the chart of accounts and dimensions'),
    // PERIOD CONTROL IS MFA-MANDATORY, on the argument report.manage and
    // workflow.manage already make. Reopening a closed period is the one action
    // that makes a settled figure move again, and BR-FIN-06 requires it to be
    // audited. The audit says who; MFA says it was really them.
    permission('finance.period.manage', 'Open, close and reopen accounting periods', {
        mfaRequired: true,
    }),
    permission('finance.journal.read', 'View journals and the general ledger'),
    permission('finance.journal.create', 'Raise draft journals'),
    permission('finance.journal.post', 'Post a journal to the ledger'),
    permission('finance.journal.reverse', 'Reverse a posted journal'),
    // FR-LED-005. DEFINING a schedule is configuration; GENERATING is what the
    // worker does. They are separate because a person who may write a definition
    // is not thereby the person who decides it runs today — and because the
    // worker's identity should hold the narrow permission, not the broad one.
    permission('finance.recurring.read', 'View recurring journal, accrual and prepayment definitions and what they have generated'),
    permission('finance.recurring.manage', 'Create and change recurring journal, accrual and prepayment definitions'),
    permission('finance.recurring.generate', 'Generate the journals a recurring definition is due, and their reversals'),
    // SEPARATE FROM `journal.post` (FR-CUR-003). Running a revaluation chooses
    // the rate at which every foreign balance in the entity is carried, which
    // moves the profit for the period. That is a period-end decision and belongs
    // with the people who make those, not with everyone who may post a document.
    permission('finance.revaluation.run', 'Revalue foreign-currency balances at a period end and post the unrealised difference'),
    permission('finance.revaluation.read', 'View past revaluations and what they restated'),
    permission('finance.invoice.read', 'View sales invoices and receivables'),
    permission('finance.invoice.create', 'Raise and amend draft sales invoices'),
    permission('finance.invoice.issue', 'Issue a sales invoice, allocating its number and posting it'),
    permission('finance.invoice.void', 'Void a sales invoice that was never issued'),
    // SEPARATE FROM `issue`, deliberately (FR-ARC-003). Holding an invoice out of
    // collections changes what a customer is chased for; somebody trusted to
    // raise and issue invoices is not, by that fact, trusted to stop one being
    // collected. The same permission ends the hold, because resuming collections
    // is the same decision taken the other way.
    permission('finance.invoice.dispute', 'Hold a sales invoice out of collections, and resolve the hold'),
    // SEPARATE FROM `issue`, and for a different reason than `dispute`. Issuing is
    // an internal accounting act — it allocates a number and posts a journal, and
    // a credit note reverses it. Sending is the only act in the sales-invoice path
    // that LEAVES THE BUILDING: it puts the tenant's name, its VAT number and a
    // demand for money in front of an outside party, and nothing the system does
    // afterwards can retract it. Somebody trusted to prepare and post an invoice
    // is not, by that fact, trusted to mail it to a customer.
    permission('finance.invoice.send', 'Send an issued sales invoice document to the customer'),
    permission('finance.credit_note.create', 'Raise a credit note against an issued invoice'),
    permission('finance.credit_note.issue', 'Issue a credit note and post it'),
    permission('finance.receipt.record', 'Record money received from a customer'),
    permission('finance.receipt.allocate', 'Allocate receipts and credits against invoices'),
    permission('finance.bill.read', 'View supplier bills and payables'),
    permission('finance.bill.create', 'Enter supplier bills'),
    permission('finance.bill.approve', 'Approve a supplier bill for posting'),
    permission('finance.bill.post', 'Post an approved supplier bill to the ledger'),
    // A supplier credit is the supplier's own credit note, which we RECORD rather
    // than issue: they decided it. Recording it posts it, so it is a single
    // permission and a separate one from entering a bill — reducing what we owe
    // is not the same authority as recording what we owe.
    permission('finance.supplier_credit.read', 'View credits received from suppliers'),
    permission('finance.supplier_credit.record', 'Record a credit note received from a supplier'),
    permission('finance.payment.record', 'Record money paid to a supplier'),
    permission('finance.payment.allocate', 'Allocate payments and supplier credits against bills'),
    permission('finance.bank.read', 'View bank accounts, statements and transactions'),
    permission('finance.bank.import', 'Import bank statements and transactions'),
    permission('finance.bank.reconcile', 'Match bank transactions and complete a reconciliation'),
    // CONFIGURATION, not the day's work (FR-REC-002). A matching rule changes
    // what every future suggestion in the entity looks like, for everybody;
    // somebody trusted to tick matches off a list is not by that fact trusted to
    // decide what appears on it.
    permission('finance.match_rule.read', 'View the tenant’s bank matching rules and how often they are accepted'),
    permission('finance.match_rule.manage', 'Create and change bank matching rules. A rule suggests and ranks; it never confirms.'),
    // FR-REC-007: unreconciling is an audited action with a reason, and it is a
    // SEPARATE permission from reconciling. The person who may match money is not
    // automatically the person who may unpick a completed reconciliation.
    permission('finance.bank.unreconcile', 'Unmatch a reconciled item, with a reason'),
    permission('finance.vat.read', 'View VAT periods, returns and their workings'),
    permission('finance.vat.prepare', 'Prepare a VAT return from the ledger'),
    // The person who submits carries the penalty (SRS §2: "AI prepares, a human
    // accepts"), so this is step-up as well as MFA — a fresh proof of presence at
    // the moment of filing, not a session that was strong an hour ago.
    permission('finance.vat.submit', 'Accept the declaration and submit a VAT return', {
        mfaRequired: true,
        stepUpRequired: true,
    }),
    permission('finance.customer.read', 'View customer financial records'),
    permission('finance.customer.manage', 'Maintain customer terms, credit limits and coding'),
    permission('finance.supplier.read', 'View supplier financial records'),
    permission('finance.supplier.manage', 'Maintain supplier terms and coding'),
    // BR-FIN-12 and FR-PTY-007: a change to supplier bank details is the highest
    // value fraud target in a finance system. Its own permission, MFA-mandatory
    // and step-up, separate from ordinary supplier maintenance.
    permission('finance.supplier.bank_details', "Change a supplier's bank details", {
        mfaRequired: true,
        stepUpRequired: true,
    }),
    permission('finance.report.read', 'Run Finance reports: trial balance, ageing, ledger activity'),
];
const BY_KEY = new Map(PERMISSIONS.map((definition) => [definition.key, definition]));
export function isRegisteredPermission(key) {
    return BY_KEY.has(key);
}
export function permissionDefinition(key) {
    return BY_KEY.get(key);
}
/** Permissions whose holders must have MFA regardless of tenant policy. */
export const MFA_REQUIRED_PERMISSIONS = PERMISSIONS.filter((definition) => definition.mfaRequired === true).map((definition) => definition.key);
/** Permissions that demand a fresh proof of presence at the moment of use. */
export const STEP_UP_REQUIRED_PERMISSIONS = PERMISSIONS.filter((definition) => definition.stepUpRequired === true).map((definition) => definition.key);
export class UnregisteredPermissionError extends Error {
    constructor(key) {
        super(`Permission ${JSON.stringify(key)} is not registered. Declare it in ` +
            'packages/platform/authz/src/permissions.ts and in the migration that seeds the ' +
            'catalogue — a permission invented at a call site is one no review ever saw.');
        this.name = 'UnregisteredPermissionError';
    }
}
//# sourceMappingURL=permissions.js.map